// External Libraries
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';

// Assets
import '../styles/ForgotPassword.css';

// Types
import { InternalServerError } from '../types/types';

// Utilities
import { ErrorMessage, validatePassword } from '../utils/CommonUtils';

// Components
import Label from './common/Label';
import Input from './common/Input';
import Button from './common/Button';
import PasswordInput from './common/PasswordInput';
import Loading from './common/Loading';
import Text from './common/Text';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [confirmationCode, setConfirmationCode] = useState<string[]>([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState<'initiate' | 'verify' | 'complete'>(
    'initiate',
  );
  const [message, setMessage] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean | null>(null);
  const [resendLoading, setResendLoading] = useState<boolean | null>(null);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPassword = async (code: string) => {
    if (!isValidEmail(email)) {
      setError(
        email
          ? 'Please enter a valid email address.'
          : 'Please enter your email address.',
      );
      setLoading(false);
      return;
    }
    if (code === 'forgot') {
      setLoading(true);
      setMessage(null);
    }
    if (code === 'resend') {
      setResendLoading(true);
      setResetMessage(null);
      setPasswordError(null);
    }
    try {
      const output = await resetPassword({ username: email });

      // Handle different reset password steps
      switch (output.nextStep.resetPasswordStep) {
        case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
          if (code === 'forgot') {
            setMessage(
              `Confirmation code was sent to ${output.nextStep.codeDeliveryDetails.destination}`,
            );
            setError(null);
          } else if (code === 'resend') {
            setResetMessage(
              `Re-Confirmation code was sent to ${output.nextStep.codeDeliveryDetails.destination}`,
            );
            setResetError(null);
          } else {
            console.log('no code found');
          }
          setResendLoading(false);
          setLoading(false);
          setTimeout(() => {
            setStep('verify');
          }, 2000);
          break;
        case 'DONE':
          console.log('Successfully reset password.');
          setLoading(false);
          break;
        default:
          console.log('reset password output: ', output);
          setLoading(false);
      }
      setError(null);
    } catch (err) {
      console.error('Error initiating forgot password:', err);
      const error = err as InternalServerError;
      if (code === 'resend') {
        if (
          error.message.includes('Username/client id combination not found.')
        ) {
          setError(
            'An account with this email does not exist. Enter a different email or',
          );
        } else if (
          error.message.includes(
            'Attempt limit exceeded, please try after some time',
          )
        ) {
          setResetError(error.message);
        } else if (error.message.includes('A network error has occurred.')) {
          toast.error(
            'You are offline. Please check your internet connection.',
          );
        } else {
          setResetError(error.message);
        }
        setResetMessage(null);
        setLoading(false);
        setResendLoading(false);
      } else if (code === 'forgot') {
        if (
          error.message.includes('Username/client id combination not found.')
        ) {
          setError(
            'An account with this email does not exist. Enter a different email or',
          );
        } else if (
          error.message.includes(
            'Attempt limit exceeded, please try after some time',
          )
        ) {
          setError(error.message);
        } else if (error.message.includes('A network error has occurred.')) {
          toast.error(
            'You are offline. Please check your internet connection.',
          );
        } else {
          setError(error.message);
        }
        setMessage(null);
        setResendLoading(false);
        setLoading(false);
      } else {
        console.log('no code found');
      }
    }
  };

  const handleVerify = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    // https://docs.aws.amazon.com/cognito/latest/developerguide/how-to-recover-a-user-account.html#forgot-password
    if (confirmationCode.join('').length !== 6) {
      setResetError('Please enter a 6-digit confirmation code.');
      setResetMessage(null);
      setLoading(false);
      return;
    }
    const pwdError = await validatePassword(newPassword);
    setPasswordError(pwdError);
    if (pwdError === null) {
      try {
        await confirmResetPassword({
          username: email,
          confirmationCode: confirmationCode.join(''),
          newPassword,
        });
        setResetMessage('Password has been reset successfully');
        setLoading(false);
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } catch (err) {
        const error = err as InternalServerError;
        console.error('Error verifying code:', error);
        setResetError(null);
        if (
          error.message.includes(
            'Invalid verification code provided, please try again.',
          )
        ) {
          setResetError(
            'Invalid confirmation code. Please enter a valid code.',
          );
          setResetMessage(null);
          setLoading(false);
        } else if (
          error.message.includes(
            'Attempt limit exceeded, please try after some time.',
          )
        ) {
          setResetError(
            'You have reached the maximum number of attempts. Please wait and try again.',
          );
          setResetMessage(null);
          setLoading(false);
        } else if (error.message.includes('A network error has occurred.')) {
          toast.error(
            'You are offline. Please check your internet connection.',
          );
          setResetMessage(null);
          setLoading(false);
        } else {
          setResetError(error.message);
          setResetMessage(null);
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...confirmationCode];
      newCode[index] = value;
      setConfirmationCode(newCode);
      setResetError(null);
      setResetMessage(null);
      setPasswordError(null);
      if (value) {
        if (index < confirmationCode.length - 1) {
          const nextInput = document.getElementById(`otp-input-${index + 1}`);
          nextInput?.focus();
        } else {
          const passwordInput = document.getElementById('password-input');
          passwordInput?.focus();
        }
      }
    }
  };

  const handleResendCode = async () => {
    await handleForgotPassword('resend');
  };

  return (
    <div className="common-container padding-container custom-background">
      {step === 'initiate' && (
        <div className="auth-container">
          <div className="grid gap-32">
            <div className="grid gap-8">
              <Text className="display-extra-small">Forgot Password</Text>
              <Text className="paragraph">
                Enter the email address you used when you joined and we’ll send
                you instructions to reset your password.
              </Text>
            </div>
            <div className="grid gap-24">
              <div className="grid gap-6">
                <Label htmlFor="email" className="email-label">
                  Email<sup className="fs-12">*</sup>
                </Label>
                <Input
                  type="email"
                  className="email-input"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                    setMessage(null);
                  }}
                  placeholder="Enter your email"
                />
                {error && (
                  <div className="error-message">
                    <p>{<ErrorMessage message={error} />}</p>
                  </div>
                )}
                {message && (
                  <div className="auth-success-message">
                    <p>{message}</p>
                  </div>
                )}
              </div>
              <Button
                onClick={() => handleForgotPassword('forgot')}
                type="button"
                className="tertiary-button"
                disabled={loading != null && loading}
              >
                {loading ? (
                  <>
                    <Loading size={25} /> &nbsp; Send Reset Instructions
                  </>
                ) : (
                  'Send Reset Instructions'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'verify' && (
        <div className="auth-container">
          <div className="grid gap-32">
            <div className="grid gap-8">
              <Text className="display-extra-small">Reset your password</Text>
              <Text className="paragraph">
                We have sent a password reset code by email. Enter it below and
                set a new password.
              </Text>
            </div>
            <form onSubmit={handleVerify} className="padding-0">
              <div className="grid gap-24">
                <div className="grid gap-6">
                  <div className="nav-bar">
                    <Label htmlFor="confirmation-code" className="confirm-code">
                      Confirmation code<sup className="fs-12">*</sup>
                    </Label>
                    <Button
                      type="button"
                      onClick={handleResendCode}
                      className="resend-code"
                      disabled={resendLoading != null && resendLoading}
                    >
                      {resendLoading ? (
                        <>
                          <Loading size={10} color="var(--primary-color)" />{' '}
                          Resend code
                        </>
                      ) : (
                        'Resend code'
                      )}
                    </Button>
                  </div>
                  <div className="code-inputs">
                    {confirmationCode.map((code, index: number) => (
                      <Input
                        key={index}
                        id={`otp-input-${index}`}
                        type="text"
                        inputMode="numeric"
                        className="code-input"
                        name={`code-${index}`}
                        max={1}
                        onChange={(e) =>
                          handleCodeChange(index, e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !code && index > 0) {
                            const prevInput = document.getElementById(
                              `otp-input-${index - 1}`,
                            );
                            prevInput?.focus();
                          }
                        }}
                        onFocus={(e) => {
                          const input = e.target as HTMLInputElement;
                          const valueLength = input.value.length;
                          input.setSelectionRange(valueLength, valueLength);
                        }}
                        value={code}
                        placeholder="0"
                        custom={true}
                      />
                    ))}
                  </div>
                </div>
                <div className="grid gap-6">
                  <Label htmlFor="password" className="medium-text">
                    New Password<sup className="fs-12">*</sup>
                  </Label>
                  <PasswordInput
                    id="password-input"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setResetError(null);
                      setResetMessage(null);
                      setPasswordError(null);
                    }}
                    placeholder="••••••••"
                    autoComplete="off"
                  />
                  <Label htmlFor="password" className="text-small-regular">
                    Min 8 characters and include 1 number, 1 special character,
                    and 1 uppercase and 1 lowercase
                  </Label>
                  {resetError && (
                    <div className="error-message">
                      <p>{resetError}</p>
                    </div>
                  )}
                  {resetMessage && (
                    <div className="auth-success-message">
                      <p>{resetMessage}</p>
                    </div>
                  )}
                  {passwordError !== null && (
                    <div className="error-message">
                      Please provide the password as per above guidelines
                    </div>
                  )}
                  {/* {error && (
                  <div className="error-message">
                    <p>{<ErrorMessage message={error} />}</p>
                  </div>
                )} */}
                </div>
                <Button
                  type="submit"
                  className="tertiary-button"
                  disabled={loading != null && loading}
                >
                  {loading ? (
                    <>
                      <Loading size={25} /> &nbsp; Reset Password
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;
